import { FC, ReactNode, useCallback } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  siteSessionAccessTokenKeyName,
  siteSessionExpiresAt,
  siteSessionIdTokenKeyName,
  siteSessionRefresh,
  siteSessionTokenKeyName,
} from "@/config/environment";
import { useGroupsSteps } from "@/hooks/useGroupsSteps.ts";
import { useTokenRefresh } from "@/hooks/useTokenRefresh.ts";
import * as authServices from "@/services/auth";
import { getCookie, setCookie } from "@/tools/cookies.ts";
import { identifyUser, resetPostHogUser } from "@/tools/posthog";

import AuthContext, {
  IAuthContext,
  initialAuthContext,
} from "./auth.context.ts";
import {
  clearAuthCookies,
  clearAuthSession,
  redirectToSignIn,
  resetSessionAndRedirectToSignIn,
  setSessionExpiry,
} from "./session.ts";
import { parseProfileFromIdToken } from "./token.ts";

interface IAuthProviderProps {
  children: ReactNode;
}

const POSTHOG_TRACKING_GROUP = "PosthogTracking";

const AuthProvider: FC<IAuthProviderProps> = ({ children }) => {
  const client = useQueryClient();
  const clear = useGroupsSteps((state) => state.clear);

  const hydrateUserProfile = useCallback(async () => {
    const idToken = getCookie(siteSessionIdTokenKeyName);

    if (!idToken) {
      return initialAuthContext.profile;
    }

    const userData = parseProfileFromIdToken(idToken);

    if (userData.email) {
      identifyUser(userData.email.toLowerCase(), {
        email: userData.email,
        name: userData.name,
        is_internal: userData.email.endsWith("@somosradar.com"),
        posthog_tracking_enabled: userData.groups.includes(
          POSTHOG_TRACKING_GROUP,
        ),
      });
    }

    client.setQueryData(["fetchUserData"], userData);
    return userData;
  }, [client]);

  useTokenRefresh(
    async () => {
      const sessionExpiresAt = getCookie(siteSessionExpiresAt);
      if (sessionExpiresAt && Date.now() > Number(sessionExpiresAt)) {
        resetSessionAndRedirectToSignIn();
        return;
      }

      const refreshTokenValue = getCookie(siteSessionRefresh);

      if (!refreshTokenValue) {
        return;
      }

      const response = await authServices.refreshToken(refreshTokenValue);

      setCookie(siteSessionTokenKeyName, response.idToken);
      setCookie(siteSessionIdTokenKeyName, response.idToken);
      setCookie(siteSessionAccessTokenKeyName, response.accessToken);

      if (response.refreshToken) {
        setCookie(siteSessionRefresh, response.refreshToken);
      }

      await hydrateUserProfile();
    },
    {
      disabled: !getCookie(siteSessionRefresh),
      refreshOnMount: false,
    },
  );

  const { data: profile = initialAuthContext.profile, refetch: fetchUserData } =
    useQuery({
      queryKey: ["fetchUserData"],
      queryFn: async () => {
        const token = getCookie(siteSessionTokenKeyName);
        const idToken = getCookie(siteSessionIdTokenKeyName);

        if (!token || !idToken) {
          return initialAuthContext.profile;
        }

        return hydrateUserProfile();
      },
      staleTime: Infinity,
    });

  const login = useCallback(
    async ({ username, password }: { username: string; password: string }) => {
      try {
        const result = await authServices.login({ username, password });

        if (result.type === "NEW_PASSWORD_REQUIRED") {
          return result;
        }

        setCookie(siteSessionTokenKeyName, result.idToken);
        setCookie(siteSessionIdTokenKeyName, result.idToken);
        setCookie(siteSessionAccessTokenKeyName, result.accessToken);

        if (result.refreshToken) {
          setCookie(siteSessionRefresh, result.refreshToken);
        }

        setSessionExpiry();
        await hydrateUserProfile();
      } catch (error) {
        clearAuthCookies();
        throw error;
      }
    },
    [hydrateUserProfile],
  ) as IAuthContext["login"];

  const logout = useCallback(() => {
    clearAuthSession();
    client.setQueryData(["fetchUserData"], initialAuthContext.profile);
    client.clear();
    clear();
    resetPostHogUser();
    redirectToSignIn({ preserveReturnTo: false });
  }, [client, clear]);

  const userHasGroup = useCallback(
    (groups: string[]) => {
      return groups.some((group) => profile?.groups?.includes(group));
    },
    [profile],
  );

  return (
    <AuthContext.Provider
      value={{
        fetchUserData,
        login,
        logout,
        profile,
        userHasGroup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
